import { useEffect, useState } from 'react';
import { Button, Grid, Stack, Typography } from '@mui/material';
import { useSearchParams } from 'react-router';
import { EditFeedbackDialog } from './edit-feedback-dialog';
import { Feedback } from './feedback';
import { ProcessingFeedback } from './processing-feedback';
import EmptyStateFeedbacks from '@/assets/images/svg/empty-state-feedbacks.svg';
import { ContentBox } from '@/components/shared/content-box';
import { StateMessage } from '@/components/shared/state-message';
import { useShowUserFeedback } from '@/data/guidance/feedback';
import { UserFeedbackStatus } from '@/data/guidance/feedback/constants';
import { CardWithError } from '@/packages/skill-management/components/card-with-error';
import { Skeleton } from '@/packages/skill-management/components/skeleton';
import { SkillsList } from '@/packages/skill-management/components/skills-list';
import { UpdateSkillsDialog } from '@/packages/skill-management/components/update-skills-dialog';
import { useSkills } from '@/packages/skill-management/context/skills';

const ProcessingFeedbackContent = ({
  feedbackId,
  onError,
}: {
  feedbackId?: number;
  onError?: () => void;
}) => (
  <ContentBox>
    <ProcessingFeedback centered feedbackId={feedbackId} onError={onError} />
  </ContentBox>
);

const EmptyFeedback = ({ onClick }: { onClick: () => void }) => (
  <StateMessage
    title="Personalize seu desenvolvimento"
    description="Envie seu feedback e receba recomendações personalizadas"
    image={EmptyStateFeedbacks}
    imageSize={{ width: 64, height: 64 }}
    action={{
      onClick: onClick,
      label: 'Adicionar feedback',
    }}
  />
);

export const Feedbacks = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const novoFeedback = !!searchParams.get('novo-feedback');
  const [editFeedbackDialogOpen, setEditFeedbackDialogOpen] =
    useState(novoFeedback);
  const [updateSkillsDialogOpen, setUpdateSkillsDialogOpen] = useState(false);
  // Set right after creating/replacing a feedback, so UpdateSkillsDialog streams
  // the recommendation itself instead of showing a separate processing dialog.
  const [streamingFeedbackId, setStreamingFeedbackId] = useState<
    number | undefined
  >(undefined);
  // The channel can fail purely inside the websocket (e.g. an unhandled backend
  // error) without ever flipping inference_status to "failed" in the DB, so the
  // page can't rely on REST status alone to know processing failed.
  const [hasStreamingError, setHasStreamingError] = useState(false);
  const { feedbackSkills, userFeedback, isLoadingUserFeedback } = useSkills();
  const { refetch: refetchUserFeedback } = useShowUserFeedback();

  const status = userFeedback?.inference_status;
  const isFailed = status === UserFeedbackStatus.Failed || hasStreamingError;
  const isProcessing =
    (status === UserFeedbackStatus.Processing || !status) && !hasStreamingError;
  const hasUserFeedback = Boolean(userFeedback?.id);

  useEffect(() => {
    setHasStreamingError(false);
  }, [userFeedback?.id]);

  const checkedSkills = feedbackSkills?.filter((skill) => skill.checked) || [];
  const uncheckedSkills =
    feedbackSkills?.filter((skill) => !skill.checked) || [];

  const dialogDescription = (
    <>
      <Typography variant="body1">{userFeedback?.generated_summary}</Typography>
      <Typography variant="h3" my={3}>
        Selecione as habilidades a trabalhar
      </Typography>
    </>
  );

  const openEditDialog = () => setEditFeedbackDialogOpen(true);
  const closeEditDialog = () => {
    setEditFeedbackDialogOpen(false);
    searchParams.delete('novo-feedback');
    setSearchParams(searchParams, { replace: true });
  };
  const openUpdateSkillsDialog = () => setUpdateSkillsDialogOpen(true);
  const closeUpdateSkillsDialog = () => {
    setUpdateSkillsDialogOpen(false);
    setStreamingFeedbackId(undefined);
  };

  const handleEditSuccess = (feedbackId: number) => {
    closeEditDialog();
    setStreamingFeedbackId(feedbackId);
    openUpdateSkillsDialog();
  };

  const handleStreamingRetry = () => {
    closeUpdateSkillsDialog();
    openEditDialog();
  };

  const renderContent = () => {
    if (isLoadingUserFeedback) return <Skeleton />;

    if (isFailed) {
      return <CardWithError action={openEditDialog} />;
    }

    if (!hasUserFeedback) return <EmptyFeedback onClick={openEditDialog} />;

    if (isProcessing && !updateSkillsDialogOpen)
      return (
        <ProcessingFeedbackContent
          feedbackId={userFeedback?.id}
          onError={() => {
            setHasStreamingError(true);
            refetchUserFeedback();
          }}
        />
      );

    return (
      <ContentBox>
        <Grid container spacing={{ xs: 3, md: 6 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Feedback onEditFeedback={openEditDialog} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }} flex={1}>
            <Typography variant="h3">Habilidades em foco</Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" my={2}>
              <SkillsList
                onClick={openUpdateSkillsDialog}
                checkedSkills={checkedSkills}
                uncheckedSkills={uncheckedSkills}
              />
            </Stack>
            <Button onClick={openUpdateSkillsDialog}>Editar habilidades</Button>
          </Grid>
        </Grid>
      </ContentBox>
    );
  };

  return (
    <>
      {renderContent()}
      {editFeedbackDialogOpen && (
        <EditFeedbackDialog
          onClose={closeEditDialog}
          onSuccess={handleEditSuccess}
        />
      )}
      {updateSkillsDialogOpen && (
        <UpdateSkillsDialog
          title="Resumo do seu feedback"
          skills={feedbackSkills || []}
          onClose={closeUpdateSkillsDialog}
          description={dialogDescription}
          feedbackId={streamingFeedbackId}
          onRetry={handleStreamingRetry}
        />
      )}
    </>
  );
};
